<template>
  <SubHeading :home="home" :tabs="tabs" :shorts="false" :currTab="currTab" @active="handleActive" />

  <!-- breadcrumbs -->
  <nav class="sba-collection-breadcrumb z-depth-0">
    <div class="container nav-wrapper">
      <div class="sba-collection-breadcrumb-row">
        <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
          <i class="material-icons">home</i>
          <span>Dashboard</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <router-link to="/public" class="breadcrumb sba-collection-crumb blue-grey-text text-darken-3">
          <span>Schemes Home</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <span class="breadcrumb sba-collection-crumb blue-grey-text pointable">
          <span>Upload List</span>
        </span>
        <span class="sba-collection-context">Scheme Enrollee Upload</span>
      </div>
    </div>
  </nav>

  <div class="sba-collection-page-surface is-workbench public-add-surface">
    <div class="container">
      <LoadingDiv v-if="!isReady" />

      <div class="public-add-workbench sba-slide-in sba-slide-in-children" v-else>
        <section class="public-add-head">
          <div class="public-add-title">
            <span class="secondary-page-signifier">
              <button
                type="button"
                class="secondary-page-back"
                title="Back to Schemes Home"
                @click.prevent="handleActive(home)"
              >
                <i class="material-icons">keyboard_return</i>
              </button>
              <button type="button" class="secondary-parent-link" @click.prevent="handleActive(home)">
                Schemes Home
              </button>
              <small>Enrollee list upload</small>
            </span>
            <label class="active teal-text">Scheme Upload</label>
            <h5>{{ client?.name || "Scheme List" }}</h5>
            <p>
              Upload an Excel enrollee list and assign the scheme period before sending it for processing.
            </p>
          </div>

          <span class="public-upload-pill">
            <i class="material-icons">description</i>
            Excel upload
          </span>
        </section>

        <section class="public-add-body sba-slide-in-children">
          <form @submit.prevent="handleSubmit">
            <div class="public-form-card">
              <div class="file-field input-field">
                <div class="btn black">
                  <span>New List</span>
                  <input type="file" @change="handleFile" />
                </div>
                <div class="file-path-wrapper">
                  <input class="file-path" v-model="inputFile" type="text" />
                  <p class="red-text" v-if="fileError">{{ fileError }}</p>
                </div>
              </div>
            </div>

            <div class="public-form-card" v-if="file">
              <div class="input-field">
                <select v-model="scheme">
                  <option value="" disabled selected></option>
                  <option :value="client?.scheme.id">
                    {{ client?.scheme.name }}
                  </option>
                </select>
                <label>Scheme of List</label>
              </div>
            </div>

            <div class="public-form-card" v-if="file && scheme">
              <div class="input-field" @click="(periodStart = null), (periodEnd = null)">
                <select v-model="periodStart">
                  <option value="" disabled selected></option>
                  <option :value="nextMonthDate">{{ nextMonth }}</option>
                  <option :value="thisMonthDate">
                    {{ thisMonth }} (Current month)
                  </option>
                  <option value="">-</option>
                  <option :value="pastMonth.date" v-for="pastMonth in pastInactiveMonths" :key="pastMonth.date">
                    {{ pastMonth.month }}
                  </option>
                </select>
                <label>Period of List</label>
              </div>
            </div>

            <div class="public-form-card" v-if="file && scheme && periodStart">
              <div class="input-field">
                <select v-model="periodEnd">
                  <option value="" disabled selected></option>
                  <option :value="endMonth.date" v-for="endMonth in endMonths" :key="endMonth.date">
                    {{ endMonth.month }}
                  </option>
                </select>
                <label>Select End Month</label>
              </div>
            </div>

            <p class="red-text" v-if="uploadError"><br />{{ uploadError }}</p>

            <div class="public-submit-row">
              <button class="waves-effect waves-light btn-large" :class="{
                disabled:
                  !scheme || !periodStart || !periodEnd || isSubmitting,
              }">
                <i class="material-icons right">upload</i>{{ isSubmitting ? `Uploading (${uploadProgress})` : "Upload"
                }}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, onUpdated, ref, watchEffect } from "vue";
import { useRouter } from "vue-router";
import useModuleNavigation from "@/composables/useModuleNavigation";
import getItem from "@/composables/getItem";
import useStorage from "@/composables/useStorage";
import {
  activeMonths,
  pastMonths,
  generateEndMonths,
} from "@/composables/fxLibrary/months";
import SubHeading from "@/components/HeaderSubDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import M from "materialize-css";

export default {
  name: "publicAdd",
  components: { SubHeading, LoadingDiv },
  props: ["id"],
  setup(props) {
    const cat = ref("schemes");
    const home = ref(null);
    const isReady = ref(false);
    const client = ref(null);
    const error = ref(null);
    const isSubmitting = ref(false);
    const file = ref(null);
    const inputFile = ref(null);
    const fileError = ref(null);
    const scheme = ref(null);
    const thisMonth = ref(null);
    const nextMonth = ref(null);
    const thisMonthDate = ref(null);
    const nextMonthDate = ref(null);
    const pastInactiveMonths = ref([]);
    const endMonths = ref([]);
    const periodStart = ref(null);
    const periodEnd = ref(null);
    const types = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    const currTab = ref(null);
    const tabs = ref([]);

    // router
    const router = useRouter();
    const { routeToTab } = useModuleNavigation();

    // storage
    const { uploadFile, uploadProgress, uploadError, data, completed } =
      useStorage();

    // title
    onMounted(() => {
      // page name
      document.title = "Scheme List Upload";
    });

    // menu
    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "Schemes", title: "All", icon: "arrow_back", url: "public", hash: null, });
      tabs.value.push({ id: 2, name: "Schemes", title: "Upload List", icon: null, url: null, hash: null });

      // set default
      currTab.value = tabs.value.find(x => x.title.includes("Add"));
      // set home
      home.value = tabs.value.find(x => x.id === 1);
    });

    // handle curr status
    const handleActive = (tab) => {
      routeToTab(tab, { replace: !tab?.url });
    };

    // get client
    const getClient = () => {
      (async () => {
        const { res, err } = await getItem("client", props.id);

        if (res.value) {
          if (res.value.data.cat != cat.value) {
            // refer back home
            router.push({
              name: "home",
            });
          } else {
            // assign
            client.value = res.value.data;
          }
        } else {
          error.value = err.value;
          // back to public
          router.push({
            name: "public",
          });
        }

        // page is ready
        isReady.value = true;
      })();
    };

    // select
    onUpdated(() => {
      // activate select field
      const elemsSelect = document.querySelectorAll("select");
      M.FormSelect.init(elemsSelect, {});
    });

    // Get client
    onMounted(() => {
      // get client details
      getClient();
    });

    // month initialization
    onMounted(() => {
      // active
      const { fx: active } = activeMonths();
      // active months
      thisMonth.value = active.value.thisMonth;
      thisMonthDate.value = active.value.thisMonthDate;
      nextMonth.value = active.value.nextMonth;
      nextMonthDate.value = active.value.nextMonthDate;

      // past
      const { fx: past } = pastMonths();
      // past month array
      pastInactiveMonths.value = past.value;
    });

    // clear
    const clearForm = () => {
      file.value = null; //clear file value
      inputFile.value = null; // Clear the file input value
      isSubmitting.value = false;
      uploadProgress.value = "0%";
      scheme.value = null;
      periodStart.value = null;
      periodEnd.value = null;
    };

    // file
    const handleFile = (e) => {
      const selected = e.target.files[0];
      if (selected && types.includes(selected.type)) {
        file.value = selected;
        inputFile.value = file.value.name;
        fileError.value = null;
      } else {
        file.value = null;
        inputFile.value = null;
        fileError.value = "Please select an Excel file (.XLSX, XLS)";
      }
      scheme.value = null;
      periodStart.value = null;
      periodEnd.value = null;
    };

    // watch periodStart pick
    watchEffect(() => {
      if (periodStart.value) {
        // finish
        const { fx: endings } = generateEndMonths(periodStart.value);
        // end month array
        endMonths.value = endings.value;
      } else {
        endMonths.value = [];
      }
    });

    // handle submit
    const handleSubmit = async () => {
      if (
        file.value &&
        scheme.value &&
        (periodStart.value || periodEnd.value)
      ) {
        // Start Uploading
        isSubmitting.value = true;
        uploadError.value = null;

        // Cloud Function
        const fx = "enrolleeLoader";

        // Round up
        const cloudInput = {
          scheme: scheme.value,
          start: periodStart.value,
          end: periodEnd.value,
        };

        // upload
        uploadFile(file.value, fx, cloudInput);
      } else {
        // error
        clearForm();
      }
    };

    // watch tariff upload
    watchEffect(() => {
      if (completed.value) {
        // clear
        clearForm();
        // data.succ.Success, data.value.succ.inserted_rows, data.succ.Error
        router.push({
          name: "public",
          rows: data.value.succ.inserted_rows,
        });
      }
      if (uploadError.value) {
        // clear
        clearForm();
      }
    });

    return {
      handleActive,
      home,
      currTab,
      tabs,
      cat,
      handleFile,
      isReady,
      isSubmitting,
      handleSubmit,
      error,
      uploadProgress,
      fileError,
      uploadError,
      file,
      inputFile,
      scheme,
      thisMonth,
      thisMonthDate,
      nextMonth,
      nextMonthDate,
      pastInactiveMonths,
      endMonths,
      periodStart,
      periodEnd,
      client,
    };
  },
};
</script>

<style scoped>
.public-add-surface {
  min-height: calc(100vh - 260px);
  padding-bottom: 56px;
}

.public-add-workbench {
  color: #263238;
  margin-top: -14px;
  padding-top: 30px;
  position: relative;
  z-index: 1;
}

.public-add-head {
  align-items: flex-end;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px 6px 0 0;
  box-shadow: 0 16px 30px -26px rgba(38, 50, 56, 0.64);
  display: flex;
  gap: 24px;
  justify-content: space-between;
  overflow: visible;
  padding: 28px 32px 24px;
  position: relative;
  z-index: 2;
}

.public-add-head::before {
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 7px 7px 0 0;
  content: "";
  height: 18px;
  left: -1px;
  pointer-events: none;
  position: absolute;
  right: -1px;
  top: -1px;
  z-index: 1;
}

.public-add-head > * {
  position: relative;
  z-index: 2;
}

.public-add-title {
  min-width: 0;
}

.secondary-page-signifier {
  align-items: center;
  background: #fff8e1;
  border: 1px solid #ffd54f;
  border-bottom-color: #fff8e1;
  border-radius: 6px 6px 0 0;
  box-shadow: 0 -8px 18px -18px rgba(121, 85, 72, 0.5);
  color: #4e342e;
  display: inline-flex;
  gap: 7px;
  font-size: 0.76rem;
  font-weight: 800;
  left: 24px;
  line-height: 1.2;
  padding: 7px 10px;
  position: absolute;
  text-transform: uppercase;
  top: -32px;
}

.secondary-page-back {
  align-items: center;
  background: #00897b;
  border: 1px solid #00796b;
  border-radius: 5px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  height: 24px;
  justify-content: center;
  margin: -2px 1px -2px -3px;
  padding: 0;
  width: 24px;
}

.secondary-page-back i {
  color: #ffffff;
  font-size: 16px;
  line-height: 16px;
}

.secondary-parent-link {
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #4e342e;
  cursor: pointer;
  font: inherit;
  font-weight: 800;
  margin: -2px 0;
  padding: 3px 4px;
  text-transform: uppercase;
  transition: background-color 0.18s ease, color 0.18s ease;
}

.secondary-parent-link:hover {
  background: rgba(255, 213, 79, 0.42);
  color: #263238;
}

.secondary-page-signifier small {
  border-left: 1px solid rgba(121, 85, 72, 0.18);
  color: #6d4c41;
  font-size: 0.72rem;
  font-weight: 700;
  margin-left: 2px;
  padding-left: 9px;
  text-transform: none;
}

.public-add-title h5 {
  color: #263238;
  font-weight: 800;
  line-height: 1.2;
  margin: 6px 0 0;
  overflow-wrap: anywhere;
}

.public-add-title p {
  color: #607d8b;
  font-size: 1.02rem;
  line-height: 1.5;
  margin: 9px 0 0;
  max-width: 760px;
}

.public-upload-pill {
  align-items: center;
  background: #e8f3f2;
  border: 1px solid #9bd2cb;
  border-radius: 999px;
  color: #00695c;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.82rem;
  font-weight: 800;
  gap: 8px;
  min-height: 40px;
  padding: 0 14px;
}

.public-upload-pill i {
  font-size: 19px;
}

.public-add-body {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 0 0 6px 6px;
  border-top: 0;
  box-shadow: 0 18px 32px -30px rgba(38, 50, 56, 0.72);
  padding: 26px;
}

.public-form-card {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 16px 28px -30px rgba(38, 50, 56, 0.7);
  margin-bottom: 14px;
  padding: 18px 20px 12px;
}

.public-form-card .input-field {
  margin-bottom: 0;
}

.public-form-card .file-field {
  margin-top: 0;
}

.public-submit-row {
  margin-top: 26px;
}

@media only screen and (max-width: 992px) {
  .public-add-head {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media only screen and (max-width: 600px) {
  .public-add-head,
  .public-add-body {
    padding: 20px;
  }

  .public-add-title h5 {
    font-size: 1.35rem;
    line-height: 1.25;
  }

  .public-add-title p {
    font-size: 0.95rem;
  }

  .secondary-page-signifier {
    align-items: flex-start;
    border-radius: 6px;
    flex-wrap: wrap;
  }

  .public-upload-pill,
  .public-submit-row .btn-large,
  .public-submit-row button {
    justify-content: center;
    width: 100%;
  }

  .public-form-card {
    padding: 16px;
  }
}
</style>
